/**
 * Clients Dual-Write Test
 *
 * Tests that client operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * C1: Create client with name only
 * C2: Create client with phone
 * C3: Create client with email
 * C4: Update client info
 * C5: Delete client
 */

import {
  addNewClient,
  updateClient,
  deleteClient,
} from '../global/Firebase/clients';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, dbAtom } from '../global/AtomsProvider';

// Test variation definitions
export const CLIENT_TEST_VARIATIONS = [
  { id: 'C1', category: 'create', name: 'Create client (name only)' },
  { id: 'C2', category: 'create', name: 'Create client with phone' },
  { id: 'C3', category: 'create', name: 'Create client with email' },
  { id: 'C4', category: 'update', name: 'Update client info' },
  { id: 'C5', category: 'delete', name: 'Delete client' },
];

// Get test categories for UI grouping
export function getClientTestCategories() {
  return [
    { id: 'create', name: 'Create Client', variations: ['C1', 'C2', 'C3'] },
    { id: 'update', name: 'Update Client', variations: ['C4'] },
    { id: 'delete', name: 'Delete Client', variations: ['C5'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getClientTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const db = jotaiStore.get(dbAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  return {
    organization,
    store,
    db,
    organizationId: organization.id,
  };
}

/**
 * Compare client via backend API
 */
async function compareClientViaApi(clientId, clientData, log) {
  try {
    const response = await api.get(`/clients/compare/${clientId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        clientId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'client',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'name', sb: 'name' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(clientData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (fsVal && sbVal && !valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    // Compare phone (Firestore uses 'phoneNumber', Supabase uses 'phone')
    const fsPhone = normalizeValue(clientData?.phoneNumber || clientData?.phone);
    const sbPhone = normalizeValue(supabaseData?.phone);
    if (fsPhone && sbPhone && fsPhone !== sbPhone) {
      differences.push({
        field: 'phone',
        supabaseValue: sbPhone,
        firestoreValue: fsPhone,
        severity: 'warning',
      });
    }

    // Compare email
    const fsEmail = normalizeValue(clientData?.email);
    const sbEmail = normalizeValue(supabaseData?.email);
    if (fsEmail && sbEmail && fsEmail !== sbEmail) {
      differences.push({
        field: 'email',
        supabaseValue: sbEmail,
        firestoreValue: fsEmail,
        severity: 'warning',
      });
    }

    return {
      clientId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      clientId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if client was deleted from Supabase
 */
async function checkClientDeleted(clientId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/clients/compare/${clientId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single client test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, organizationId } = testData;

  let createdRef = null;
  let createdClientData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'C1': {
        // Create client with name only
        const timestamp = Date.now();

        const clientData = {
          organizationId,
          name: `Test Client ${timestamp}`,
          registrationDate: new Date(),
          registrationSource: store.id,
          dailyStatID: `test-client-${timestamp}-${store.id}`,
        };

        log(`Creating client: "${clientData.name}"`);
        createdRef = await addNewClient(db, clientData);
        createdClientData = { ...clientData, id: createdRef.id };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareClientViaApi(createdRef.id, clientData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test client...');
        await deleteClient(db, createdRef.id);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'C2': {
        // Create client with phone
        const timestamp = Date.now();
        const phone = `+7${Math.floor(Math.random() * 9000000000) + 1000000000}`;

        const clientData = {
          organizationId,
          name: `Test Client Phone ${timestamp}`,
          phoneNumber: phone,
          registrationDate: new Date(),
          registrationSource: store.id,
          dailyStatID: `test-client-phone-${timestamp}-${store.id}`,
        };

        log(`Creating client: "${clientData.name}" with phone ${phone}`);
        createdRef = await addNewClient(db, clientData);
        createdClientData = { ...clientData, id: createdRef.id };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareClientViaApi(createdRef.id, clientData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test client...');
        await deleteClient(db, createdRef.id);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'C3': {
        // Create client with email
        const timestamp = Date.now();
        const email = `test${timestamp}@example.com`;

        const clientData = {
          organizationId,
          name: `Test Client Email ${timestamp}`,
          email,
          registrationDate: new Date(),
          registrationSource: store.id,
          dailyStatID: `test-client-email-${timestamp}-${store.id}`,
        };

        log(`Creating client: "${clientData.name}" with email ${email}`);
        createdRef = await addNewClient(db, clientData);
        createdClientData = { ...clientData, id: createdRef.id };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareClientViaApi(createdRef.id, clientData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test client...');
        await deleteClient(db, createdRef.id);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'C4': {
        // Update client test
        const timestamp = Date.now();

        const clientData = {
          organizationId,
          name: `Test Client Update ${timestamp}`,
          registrationDate: new Date(),
          registrationSource: store.id,
          dailyStatID: `test-client-update-${timestamp}-${store.id}`,
        };

        log('Creating client to update...');
        createdRef = await addNewClient(db, clientData);
        createdClientData = { ...clientData, id: createdRef.id };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareClientViaApi(createdRef.id, clientData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Client was not created in Supabase';
          break;
        }

        // Now update
        const updatedName = `Updated Client ${timestamp}`;
        log(`Updating client name to "${updatedName}"...`);
        await updateClient(db, createdRef.id, { name: updatedName });

        log('Waiting 6000ms for update sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if updated
        const updateCheck = await compareClientViaApi(createdRef.id, { ...clientData, name: updatedName }, log);
        result.comparisonResult = updateCheck;
        result.passed = updateCheck.foundInSupabase && updateCheck.supabaseData?.name === updatedName;

        // Cleanup
        log('Cleaning up test client...');
        await deleteClient(db, createdRef.id);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'C5': {
        // Delete client test
        const timestamp = Date.now();

        const clientData = {
          organizationId,
          name: `Test Client Delete ${timestamp}`,
          registrationDate: new Date(),
          registrationSource: store.id,
          dailyStatID: `test-client-del-${timestamp}-${store.id}`,
        };

        log('Creating client to delete...');
        createdRef = await addNewClient(db, clientData);
        createdClientData = { ...clientData, id: createdRef.id };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareClientViaApi(createdRef.id, clientData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Client was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting client...');
        await deleteClient(db, createdRef.id);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkClientDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          clientId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdRef) {
      try {
        await deleteClient(db, createdRef.id);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all client tests
 */
export async function runFullClientTest(log = console.log, selectedVariations = null) {
  log('Starting clients dual-write tests...');

  const testData = getClientTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store and to be logged in.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('CLIENTS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || CLIENT_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = CLIENT_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = CLIENT_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  CLIENT_TEST_VARIATIONS,
  getClientTestCategories,
  getClientTestData,
  runFullClientTest,
};
